// App policies: who may use an MFA API application, and whose phone gets the
// push. Policies of type "app" in the policy service (authnull-service
// internal/policy/routes.go policyService group; handler
// internal/policy/handler/policyjson_handler.go; dto.App and
// Permission.iam_users in internal/policy/dto/dto.go; matched by
// internal/apppolicy). Console API base, like the AD / DB policy screens.
//
//   ListPolicy        { orgId, tenantId, filters: [{ filterParameter: "policyType", filterValue: "app" }], limit, page }
//                     → { code, data: [PolicyJsonTable], total_pages }  (live version of each policy only)
//   CreateJSONPolicy  { orgId, tenantId, policyName, policyType: "app", policyJson, force? }
//                     → 200 { code: 200, data: "<new id>", warning?, conflicts? }
//                     → 409 { code: 409, status: "conflict", message, warning, conflicts: [PolicyJsonTable] }:
//                       not created; the same body with force: true creates it anyway
//   UpdateJSONPolicy  { id, orgId, tenantId, policyName, policyType, policyJson, updatedBy }
//                     → { updatedPolicyID: { code, status, data: "<new version id>" } }
//                     Editing makes a new version: the old id is no longer listed.
//   deletePolicyJSON  { orgId, tenantId, policyId } → { code, status, message }
//                     Always the id from the list: an older id deletes only an old version.
//
// Every helper returns a Promise; failures reject with { status, text, logout,
// invalid }. invalid is the reason from a 400 "Invalid policy: <reason>"
// (create: { code: 400, status: "failed", message }, update: { error }), and
// then text is that reason too.
import axios from "axios";

import { policyErrorMessage } from "../components/policyShell/conditionsModel";
import { actionLabel } from "../components/policyShell/signals";
import { DOMAIN_ID, ORG_ID, REACT_APP_POLICY_API, USER_EMAIL } from "../constants";
import { checkAPISuccess } from "../utils/helpers";
import { dispatchGetAllAdUsers, dispatchGetAllDoamins } from "../utils/apiservice";
import { requestsFor } from "./headerService";
import { getPlatformUserList } from "./tenantService";

const instance = axios.create({ baseURL: REACT_APP_POLICY_API });
const requests = requestsFor(instance);

const path = {
  list: "/api/v1/policyService/ListPolicy",
  create: "/api/v1/policyService/CreateJSONPolicy",
  update: "/api/v1/policyService/UpdateJSONPolicy",
  remove: "/api/v1/policyService/deletePolicyJSON",
};

export const APP_POLICY_TYPE = "app";
const PAGE = 50;
const MAX_PAGES = 40;

// What a policy does when it matches. policyFlow values as internal/apppolicy
// reads them. label is the short form (list pill, filter); longLabel is the
// shared wording (policyShell ACTION_OPTIONS) used on the form.
export const POLICY_FLOWS = [
  { value: "mfa_required", label: "Require MFA", tone: "info", desc: "The IAM user's phone gets the push." },
  { value: "allow", label: "Allow", tone: "success", desc: "Let the user in without MFA." },
  { value: "block", label: "Deny", tone: "danger", desc: "Refuse the user." },
].map((f) => ({ ...f, longLabel: actionLabel(f.value) || f.label }));
export const flowMeta = (flow) => POLICY_FLOWS.find((f) => f.value === flow) || POLICY_FLOWS[0];

const ids = () => ({ orgId: ORG_ID(), tenantId: DOMAIN_ID() });

const failure = (err, fallback) => {
  const status = err?.response?.status ?? err?.status ?? 0;
  const body = err?.response?.data ?? err?.body;
  const invalid = policyErrorMessage(err) || "";
  const text =
    status === 401
      ? "Your session has expired. Sign in again."
      : invalid || (body && typeof body === "object" && (body.error || body.message)) || err?.message || fallback;
  return { status, text: typeof text === "string" && text ? text : fallback, logout: status === 401, invalid };
};

const bodyFailure = (body, fallback) => {
  const e = new Error((body && (body.message || body.error)) || fallback);
  e.status = Number(body?.code) || 0;
  e.body = body;
  return e;
};

// The fields of a policy the screens use, read once.
export const appPolicyFields = (p) => {
  const pj = p?.policyJson || {};
  return {
    id: p?.id,
    name: p?.policyName || pj.policyName || "",
    appId: (pj.app?.apps || [])[0] || "",
    user: (pj.app?.users || [])[0] || "",
    directoryId: Number(pj.app?.directoryId) || 0,
    iamUser: (pj.permissions?.iam_users || [])[0] || "",
    flow: pj.policyFlow || "mfa_required",
    status: p?.status || "",
    updatedAt: p?.updatedAt || p?.createdAt || "",
    updatedBy: p?.updatedBy || p?.createdBy || "",
    // As stored, so an edit can keep the keys the form doesn't show.
    policyJson: pj,
  };
};

// The policyJson of one app policy: one app, one directory user, one IAM user.
export const buildAppPolicyJson = ({ name, appId, user, directoryId, iamUser, flow }) => ({
  policyName: name,
  policyType: APP_POLICY_TYPE,
  policyFlow: flow || "mfa_required",
  app: { apps: [appId], users: [user], directoryId: Number(directoryId) || 0 },
  permissions: { iam_users: [iamUser] },
});

// Every live app policy (all pages). Optionally only one app's.
export async function listAppPolicies({ appId } = {}) {
  const all = [];
  try {
    for (let page = 1; page <= MAX_PAGES; page += 1) {
      const { data } = await requests.post(path.list, {
        ...ids(),
        filters: [{ filterParameter: "policyType", filterValue: APP_POLICY_TYPE }],
        limit: PAGE,
        page,
      });
      if (!checkAPISuccess(data)) throw bodyFailure(data, "Could not read the policies.");
      const rows = Array.isArray(data?.data) ? data.data : [];
      all.push(...rows);
      const pages = Number(data?.total_pages) || 0;
      if (rows.length < PAGE || (pages && page >= pages)) break;
    }
  } catch (err) {
    throw failure(err, "Could not read the policies.");
  }
  const apps = all.filter((p) => String(p?.policyType || p?.policyJson?.policyType || "").toLowerCase() === APP_POLICY_TYPE);
  return appId ? apps.filter((p) => appPolicyFields(p).appId === appId) : apps;
}

// Policy count per app id (policyJson.app.apps[0]).
export const countByApp = (policies) => {
  const counts = {};
  (policies || []).forEach((p) => {
    const a = appPolicyFields(p).appId;
    if (a) counts[a] = (counts[a] || 0) + 1;
  });
  return counts;
};

// → { id } when created, or { conflict: { message, warning, conflicts } } on a
// 409 (nothing was created; call again with force: true to save anyway).
export async function createAppPolicy({ policyJson, force = false }) {
  try {
    const { data } = await requests.post(path.create, {
      ...ids(),
      policyName: policyJson.policyName,
      policyType: APP_POLICY_TYPE,
      policyJson,
      createdBy: USER_EMAIL() || "",
      ...(force ? { force: true } : {}),
    });
    if (Number(data?.code) === 409) return { conflict: conflictOf(data) };
    if (!checkAPISuccess(data) || String(data?.status).toLowerCase() === "failed") {
      throw bodyFailure(data, "Could not create the policy.");
    }
    return { id: data?.data, warning: data?.warning || "" };
  } catch (err) {
    if (err?.response?.status === 409) return { conflict: conflictOf(err.response.data) };
    throw failure(err, "Could not create the policy.");
  }
}

const conflictOf = (body) => ({
  message: body?.message || "Conflicting policies found",
  warning: body?.warning || "",
  conflicts: Array.isArray(body?.conflicts) ? body.conflicts : [],
});

// → { id: "<new version id>" }
export async function updateAppPolicy({ id, policyJson }) {
  try {
    const { data } = await requests.post(path.update, {
      ...ids(),
      id,
      policyName: policyJson.policyName,
      policyType: APP_POLICY_TYPE,
      policyJson,
      updatedBy: USER_EMAIL() || "",
    });
    const res = data?.updatedPolicyID || data;
    if (!checkAPISuccess(res)) throw bodyFailure(res, "Could not save the policy.");
    return { id: res?.data || id };
  } catch (err) {
    throw failure(err, "Could not save the policy.");
  }
}

export async function deleteAppPolicy(policyId) {
  try {
    const { data } = await requests.post(path.remove, { ...ids(), policyId });
    if (!checkAPISuccess(data)) throw bodyFailure(data, "Could not delete the policy.");
    return data;
  } catch (err) {
    throw failure(err, "Could not delete the policy.");
  }
}

/* ------------------------------------------------- people and directories */
// The callback wrappers in utils/apiservice share one AbortController per
// endpoint: only one call to each may be in flight per screen, and an aborted
// one never settles (callers keep a request counter).

const wrapDispatch = (dispatch, payload, fallback) =>
  new Promise((resolve, reject) =>
    dispatch({
      payload,
      onSuccess: resolve,
      onFailure: ({ message, isLogout } = {}) => reject({ status: isLogout ? 401 : 400, text: message || fallback, logout: !!isLogout }),
      onError: ({ message } = {}) => reject({ status: 500, text: message || fallback, logout: false }),
    }),
  );

const ENTRA = "EntraID";
export const directoryKind = (dir) => (dir?.identity_source === ENTRA ? "Entra ID" : "Active Directory");

// The tenant's directories (AD and Entra), by id.
export const listDirectories = () =>
  wrapDispatch(dispatchGetAllDoamins, { ...ids(), pageNo: 1, pageSize: 200, filters: {} }, "Could not read the directories.").then(
    (res) => (res?.domains || []).filter((d) => d?.status !== "Deleted"),
  );

// The account name a policy names (sAMAccountName-style): the directory's
// username without a DOMAIN\ prefix or an @suffix, as the policy matcher
// normalises it (internal/policy/repo normalizePrincipal). Entra stores the
// UPN in username, so satyam.entra@corp.onmicrosoft.com → satyam.entra.
export const accountName = (u) => stripPrincipal(u?.username || u?.logoname || u?.user_principal_name || u?.emailId || "");

// A name without a DOMAIN\ prefix or an @suffix, case kept.
const stripPrincipal = (name) => {
  let s = String(name || "").trim();
  const slash = s.lastIndexOf("\\");
  if (slash >= 0) s = s.slice(slash + 1);
  const at = s.indexOf("@");
  if (at > 0) s = s.slice(0, at);
  return s.trim();
};

// How the backend compares a policy's user with an account's username
// (internal/policy/repo normalizePrincipal, used by internal/apppolicy): both
// without a DOMAIN\ prefix or an @suffix, lower-cased. So
// satyam.entra@corp.onmicrosoft.com, CORP\satyam.entra and SATYAM.ENTRA all
// match the account satyam.entra@corp.onmicrosoft.com.
export const normalizePrincipal = (name) => stripPrincipal(name).toLowerCase();

// Directory users (AD and Entra): { users: [{ key, account, directoryId,
// directoryName, email, display, entra, username, upn, emailId }], total }.
// GetAllAdUsers' emailId filter is a substring match on the email only (not
// the username or UPN), so screens that search by name filter the loaded list
// with directoryUserMatches. username / user_principal_name / display_name /
// emailId are GetAllAdUsers' keys.
export const listDirectoryUsers = ({ search = "", pageSize = 100 } = {}) =>
  wrapDispatch(
    dispatchGetAllAdUsers,
    { ...ids(), pageNo: 1, pageSize, filters: { emailId: search || "" } },
    "Could not read the directory users.",
  ).then((res) => ({
    total: Number(res?.count) || 0,
    users: (res?.users || [])
      .map((u) => ({
        key: `${Number(u.domainId) || 0}:${accountName(u).toLowerCase()}`,
        account: accountName(u),
        directoryId: Number(u.domainId) || 0,
        directoryName: u.activeDirectoryName || "",
        email: u.emailId || u.user_principal_name || "",
        display: u.display_name || u.cn || "",
        entra: String(u.userType || "").toLowerCase() === "entrauser",
        username: u.username || "",
        upn: u.user_principal_name || "",
        emailId: u.emailId || "",
      }))
      .filter((u) => u.account),
  }));

// A loaded directory user whose username, UPN, display name or email
// contains the search (case-insensitive).
export const directoryUserMatches = (u, search) => {
  const q = String(search || "").trim().toLowerCase();
  if (!q) return true;
  return [u.account, u.username, u.upn, u.display, u.emailId, u.email].some((v) => String(v || "").toLowerCase().includes(q));
};

// Console (IAM) users: [{ id, email, name, role }]. id is listPlatformUsers'
// userId (an integer: the console has no per-user UUID).
export const listIamUsers = ({ search = "" } = {}) =>
  new Promise((resolve, reject) =>
    getPlatformUserList({
      payload: { search, page: 1, pageSize: 100 },
      onSuccess: (res) =>
        resolve(
          (res?.data || [])
            .filter((u) => u?.email)
            .map((u) => ({
              id: u.userId != null ? String(u.userId) : "",
              email: u.email,
              name: [u.firstName, u.lastName].filter(Boolean).join(" ").trim(),
              role: u.role,
            })),
        ),
      onFailure: (res) => reject(failure({ response: res }, "Could not read the IAM users.")),
    }),
  );

const ZERO_UUID = "00000000-0000-0000-0000-000000000000";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Who last changed a policy, for "Last changed by": { label, title }.
// updatedBy is whatever the policy service stored: the zero UUID for an
// API-created policy → "API"; a console user's id or email → their name or
// email (from listIamUsers); any other UUID → its first 8 characters.
export const changedByLabel = (by, iamUsers) => {
  const v = String(by || "").trim();
  if (!v) return { label: "", title: "" };
  if (v === ZERO_UUID) return { label: "API", title: "Created or changed through the API" };
  const lower = v.toLowerCase();
  const u = (iamUsers || []).find((x) => (x.id && String(x.id).toLowerCase() === lower) || String(x.email || "").toLowerCase() === lower);
  if (u) return { label: u.name || u.email, title: u.email };
  // The backend stores a fresh random id as a policy's author (createdBy :=
  // uuid.New() in policyjson_repository.go), so an unknown id names nobody:
  // show no "by" line rather than a meaningless id.
  if (UUID_RE.test(v)) return { label: "", title: "AuthNull doesn't record who made this change yet." };
  return { label: v, title: v };
};

// "test1 · Entra ID" for a directory id; "Any directory" for 0.
export const directoryLabel = (dirs, id, { entra } = {}) => {
  if (!Number(id)) return "Any directory";
  const d = (dirs || []).find((x) => Number(x.id) === Number(id));
  if (!d) return entra === undefined ? `Directory ${id}` : `Directory ${id} · ${entra ? "Entra ID" : "Active Directory"}`;
  return `${d.directoryName || d.domainName || `Directory ${id}`} · ${directoryKind(d)}`;
};
